<script setup>
  import { computed, ref, inject } from "vue";
  import { ChevronDown, ChevronRight } from "lucide-vue-next";
  import { Badge } from "@/components/ui/badge";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Switch } from "@/components/ui/switch";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { TableCell, TableRow } from "@/components/ui/table";

  const props = defineProps({
    target: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
  });

  defineEmits(["toggle"]);

  const selection = inject("selection");
  const allTargets = inject("allTargets");
  const allGroups = inject("allGroups");
  const allQuotas = inject("allQuotas");

  const targetFields = computed(() => [
    { key: "n_target", label: "Autostop", placeholder: props.target.auto_n_target?.toString() ?? "", step: "1" },
    { key: "target_max_percent", label: "Max progress %", placeholder: "120", step: "1" },
    { key: "x_quota_factor", label: "x-Quotas", placeholder: "0.5", step: "0.1" },
  ]);

  const isActive = computed(() => props.target.status === "active");

  function setActive(value) {
    const t = allTargets.value.find((x) => x.token === props.target.token);
    if (t) t.status = value ? "active" : "inactive";
  }

  const targetGroups = computed(() =>
    allGroups.value.filter(
      (group) => group.survey_target_id === props.target.survey_target_id && group.quota_type === "regular",
    ),
  );

  const modalityIds = computed(() => targetGroups.value.flatMap((group) => group.answer_quotas.map((aq) => aq.id)));

  const closedQuestions = ref(new Set());
  function isQuestionOpen(name) {
    return !closedQuestions.value.has(name);
  }
  function toggleQuestion(name) {
    const next = new Set(closedQuestions.value);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    closedQuestions.value = next;
  }

  const isPercentage = (group) => group.unit === "percentage";

  const propagateModality = (quotaId, field, value) =>
    selection.propagateModalityField(allQuotas, quotaId, field, value);
</script>

<template>
  <tbody class="border-b border-border last:border-b-0">
    <TableRow
      class="hover:bg-transparent"
      :class="!isActive && 'text-muted-foreground'">
      <TableCell>
        <Checkbox
          :model-value="selection.isTargetSelected(target.token)"
          :disabled="!isActive"
          @update:model-value="selection.toggleTarget(target, modalityIds)" />
      </TableCell>
      <TableCell>
        <Switch
          :model-value="isActive"
          @update:model-value="setActive" />
      </TableCell>
      <TableCell colspan="4">
        <div class="flex items-center gap-8">
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-2 text-left font-semibold"
            @click="$emit('toggle', target.token)">
            <component
              :is="expanded ? ChevronDown : ChevronRight"
              class="size-4 shrink-0 text-muted-foreground" />
            <span class="truncate">{{ target.name }}</span>
            <Badge
              v-if="!isActive"
              variant="outline"
              class="ml-1 font-normal text-muted-foreground">
              Inactive
            </Badge>
          </button>
          <div class="flex shrink-0 items-center gap-4">
            <label
              v-for="field in targetFields"
              :key="field.key"
              class="flex items-center gap-1.5 text-xs text-muted-foreground">
              {{ field.label }}
              <InputNumber
                :model-value="target[field.key]"
                :placeholder="field.placeholder"
                :step="field.step"
                :steppers="false"
                class="h-6 w-14 text-right text-xs text-foreground"
                @update:model-value="(v) => selection.propagateTargetField(allTargets, target.token, field.key, v)" />
            </label>
          </div>
        </div>
      </TableCell>
    </TableRow>

    <template v-if="expanded">
      <TableRow
        v-if="targetGroups.length === 0"
        class="hover:bg-transparent">
        <TableCell colspan="2" />
        <TableCell colspan="4">
          <span class="pl-6 text-muted-foreground">No active quotas for this target.</span>
        </TableCell>
      </TableRow>

      <template
        v-for="group in targetGroups"
        :key="group.id">
        <TableRow class="bg-muted/30 hover:bg-muted/30">
          <TableCell>
            <Checkbox
              :model-value="selection.isQuestionSelected(group.answer_quotas.map((m) => m.id))"
              @update:model-value="selection.toggleQuestion(group.answer_quotas.map((m) => m.id))" />
          </TableCell>
          <TableCell />
          <TableCell colspan="4">
            <button
              type="button"
              class="flex items-center gap-2 pl-6 text-left"
              @click="toggleQuestion(group.question_name)">
              <component
                :is="isQuestionOpen(group.question_name) ? ChevronDown : ChevronRight"
                class="size-4 shrink-0 text-muted-foreground" />
              <span class="font-medium">{{ group.question_name }}</span>
              <span class="text-xs text-muted-foreground">· target in {{ isPercentage(group) ? "%" : "n" }}</span>
            </button>
          </TableCell>
        </TableRow>

        <template v-if="isQuestionOpen(group.question_name)">
          <TableRow
            v-for="quota in group.answer_quotas"
            :key="quota.id"
            class="hover:bg-muted">
            <TableCell>
              <Checkbox
                :model-value="selection.isModalitySelected(quota.id)"
                @update:model-value="selection.toggleModality(quota.id)" />
            </TableCell>
            <TableCell />
            <TableCell>
              <span class="block truncate pl-12">{{ quota.answer_name }}</span>
            </TableCell>
            <TableCell>
              <InputNumber
                :model-value="quota.target_value_bidder"
                :placeholder="(isPercentage(group) ? quota.target_value : quota.target)?.toString()"
                :steppers="false"
                class="mx-auto h-7 w-14 text-center"
                @update:model-value="(v) => propagateModality(quota.id, 'target_value_bidder', v)" />
            </TableCell>
            <TableCell>
              <InputNumber
                :model-value="quota.tolerance_min_percent_bidder"
                :placeholder="(quota.tolerance_min_percent ?? group.global_min_tolerance)?.toString()"
                :steppers="false"
                class="mx-auto h-7 w-14 text-center"
                @update:model-value="(v) => propagateModality(quota.id, 'tolerance_min_percent_bidder', v)" />
            </TableCell>
            <TableCell>
              <div class="grid grid-cols-[2.5rem_4.5rem] items-baseline justify-center gap-2 tabular-nums">
                <span
                  class="text-right font-medium"
                  :title="!isPercentage(group) && quota.target_percent_bidder != null ? `${quota.target_percent_bidder} %` : undefined"
                  >{{ quota.target_bidder ?? "—" }}</span
                >
                <span class="text-muted-foreground">
                  <template v-if="quota.min_bidder != null && quota.max_bidder != null">
                    {{ quota.min_bidder }}–{{ quota.max_bidder }}
                  </template>
                </span>
              </div>
            </TableCell>
          </TableRow>
        </template>
      </template>
    </template>
  </tbody>
</template>
